import {
	createActionsHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type Store,
	type StoreActionApi,
} from 'react-sweet-state';

import { type ConversationAction } from '@atlassian/rovo-conversation-actions/types';

export type DailyBriefUnread = {
	/** ISO timestamp of when the daily brief agent message completed (last_message_timestamp). */
	completedAt: string | null;
	conversationId: string;
	name: string | null;
} | null;

export type AISmartButtonState = {
	buttonClickedTimestamp?: number;
	dailyBriefUnread: DailyBriefUnread;
	/**
	 * Whether a nudge bubble is currently visible on screen (TREX-1601).
	 * Distinct from `nudges` above, which is the QUEUE of nudges available
	 * to display. `isNudgeVisible` is the live "is one rendered right now"
	 * flag, published by `AIProactiveNudge`. Consumed by `AISmartButton`
	 * to suppress the mouse-proximity color-cycle animation while a nudge
	 * is on screen — otherwise the animation visually competes with the
	 * nudge for the same button surface.
	 */
	isNudgeVisible: boolean;
	nudges: ConversationAction[];
};

export const initialState: AISmartButtonState = {
	nudges: [],
	dailyBriefUnread: null,
	isNudgeVisible: false,
};

export const actions = {
	setNudges:
		(nudges: ConversationAction[]) =>
		({ setState }: StoreActionApi<AISmartButtonState>): void => {
			setState({ nudges });
		},
	setButtonClickedTimestamp:
		(buttonClickedTimestamp: number) =>
		({ setState }: StoreActionApi<AISmartButtonState>): void => {
			setState({ buttonClickedTimestamp });
		},
	setDailyBriefUnread:
		(dailyBriefUnread: DailyBriefUnread) =>
		({ setState }: StoreActionApi<AISmartButtonState>): void => {
			setState({ dailyBriefUnread });
		},
	setIsNudgeVisible:
		(isNudgeVisible: boolean) =>
		({ setState }: StoreActionApi<AISmartButtonState>): void => {
			setState({ isNudgeVisible });
		},
};

export const AISmartButtonStore: Store<
	AISmartButtonState,
	{
		setButtonClickedTimestamp: (
			buttonClickedTimestamp: number,
		) => ({ setState }: StoreActionApi<AISmartButtonState>) => void;
		setDailyBriefUnread: (
			dailyBriefUnread: DailyBriefUnread,
		) => ({ setState }: StoreActionApi<AISmartButtonState>) => void;
		setIsNudgeVisible: (
			isNudgeVisible: boolean,
		) => ({ setState }: StoreActionApi<AISmartButtonState>) => void;
		setNudges: (
			nudges: ConversationAction[],
		) => ({ setState }: StoreActionApi<AISmartButtonState>) => void;
	}
> = createStore({
	initialState,
	actions,
	name: 'AISmartButtonStore',
});

export const useAISmartButtonActions: HookActionsFunction<
	BoundActions<
		AISmartButtonState,
		{
			setButtonClickedTimestamp: (
				buttonClickedTimestamp: number,
			) => ({ setState }: StoreActionApi<AISmartButtonState>) => void;
			setDailyBriefUnread: (
				dailyBriefUnread: DailyBriefUnread,
			) => ({ setState }: StoreActionApi<AISmartButtonState>) => void;
			setIsNudgeVisible: (
				isNudgeVisible: boolean,
			) => ({ setState }: StoreActionApi<AISmartButtonState>) => void;
			setNudges: (
				nudges: ConversationAction[],
			) => ({ setState }: StoreActionApi<AISmartButtonState>) => void;
		}
	>
> = createActionsHook(AISmartButtonStore);
export type AISmartButtonActions = ReturnType<typeof useAISmartButtonActions>;
